import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentPreparation = () => {
  const [activeCategory, setActiveCategory] = useState('achat');

  const documentCategories = {
    achat: {
      title: "Documents d'Achat",
      icon: "ShoppingCart",
      documents: [
        {
          name: "Contrat de Vente Préliminaire",
          description: "Accord initial entre acheteur et vendeur",
          required: true,
          downloadable: true
        },
        {
          name: "Certificat de Propriété",
          description: "Preuve légale de propriété du vendeur",
          required: true,
          downloadable: false
        },
        {
          name: "Plan Cadastral",
          description: "Délimitation officielle de la propriété",
          required: true,
          downloadable: false
        },
        {
          name: "Attestation de Non-Gage",
          description: "Confirmation d'absence d'hypothèques",
          required: true,
          downloadable: true
        }
      ]
    },
    vente: {
      title: "Documents de Vente",
      icon: "Home",
      documents: [
        {
          name: "Titre de Propriété Original",
          description: "Document officiel de propriété",
          required: true,
          downloadable: false
        },
        {
          name: "Quitus Fiscal",
          description: "Attestation de paiement des impôts",
          required: true,
          downloadable: true
        },
        {
          name: "Certificat d'Urbanisme",
          description: "Conformité aux règles d'urbanisme",
          required: false,
          downloadable: true
        },
        {
          name: "Procuration (si applicable)",
          description: "Autorisation de représentation légale",
          required: false,
          downloadable: true
        }
      ]
    },
    succession: {
      title: "Documents de Succession",
      icon: "Users",
      documents: [
        {
          name: "Acte de Décès",
          description: "Certificat officiel de décès",
          required: true,
          downloadable: false
        },
        {
          name: "Testament (si existant)",
          description: "Volontés du défunt concernant ses biens",
          required: false,
          downloadable: false
        },
        {
          name: "Certificat d'Hérédité",
          description: "Identification des héritiers légaux",
          required: true,
          downloadable: true
        },
        {
          name: "Inventaire des Biens",
          description: "Liste détaillée du patrimoine immobilier",
          required: true,
          downloadable: true
        }
      ]
    }
  };

  const checklist = [
    "Vérifiez que tous les documents requis sont disponibles",
    "Assurez-vous que les copies sont certifiées conformes",
    "Préparez les pièces d'identité de toutes les parties",
    "Rassemblez les justificatifs de revenus si nécessaire",
    "Contactez votre notaire pour validation finale"
  ];

  return (
    <section className="py-24 bg-transparent border-none outline-none relative z-10">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-6 tracking-tight">
            Préparation de Documents
          </h2>
          <p className="text-lg text-white/50 max-w-2xl mx-auto font-light leading-relaxed">
            Guides complets et modèles téléchargeables pour préparer votre dossier juridique sereinement.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Category Navigation (Sidebar) */}
          <div className="lg:col-span-1">
            <div className="bg-[#111111] rounded-3xl p-6 shadow-2xl border-none outline-none sticky top-28">
              <h3 className="text-sm font-bold text-white/40 uppercase tracking-widest mb-6">
                Catégories
              </h3>
              <div className="space-y-3">
                {Object.entries(documentCategories)?.map(([key, category]) => (
                  <button
                    key={key}
                    onClick={() => setActiveCategory(key)}
                    className={`w-full flex items-center space-x-4 px-5 py-4 rounded-2xl text-left transition-all duration-300 border-none outline-none ${
                      activeCategory === key
                        ? 'bg-brand-gold text-black shadow-lg shadow-brand-gold/20' 
                        : 'bg-white/5 hover:bg-white/10 text-white/70 hover:text-white'
                    }`}
                  >
                    <Icon 
                      name={category?.icon} 
                      size={20} 
                      className={activeCategory === key ? 'text-black' : 'text-white/50'} 
                    />
                    <span className="text-sm font-bold">{category?.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Document List */}
          <div className="lg:col-span-3">
            <div className="bg-[#111111] rounded-3xl shadow-2xl border-none outline-none overflow-hidden">
              
              <div className="p-8 md:p-10 bg-white/[0.02]">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-brand-gold/10 rounded-2xl flex items-center justify-center shadow-inner">
                    <Icon 
                      name={documentCategories?.[activeCategory]?.icon} 
                      size={24} 
                      className="text-brand-gold" 
                    />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white tracking-tight">
                    {documentCategories?.[activeCategory]?.title}
                  </h3>
                </div>
              </div>

              <div className="p-8 md:p-10 space-y-4">
                {documentCategories?.[activeCategory]?.documents?.map((doc, index) => (
                  <div 
                    key={index} 
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-6 bg-white/5 rounded-2xl transition-all duration-300 hover:bg-white/10 border-none outline-none group"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-bold text-white text-lg">{doc?.name}</h4>
                        {doc?.required && (
                          <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-red-500/10 text-red-400 rounded-full shadow-sm">
                            Requis
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-light text-white/50 leading-relaxed">{doc?.description}</p>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-2 sm:mt-0">
                      {doc?.downloadable && (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Download"
                          iconPosition="left"
                          className="bg-white/5 hover:bg-white/20 text-white rounded-full border-none shadow-none transition-transform hover:scale-105 active:scale-95"
                        >
                          Télécharger
                        </Button>
                      )}
                      <div className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center flex-shrink-0">
                        <Icon 
                          name={doc?.required ? "AlertCircle" : "CheckCircle"} 
                          size={20} 
                          className={doc?.required ? "text-yellow-500" : "text-emerald-400"} 
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Checklist (Apple Style Bento) */}
                <div className="mt-12 p-8 md:p-10 bg-gradient-to-br from-brand-gold/10 to-yellow-600/5 rounded-3xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                    <Icon name="CheckSquare" size={100} className="text-brand-gold" />
                  </div>
                  
                  <div className="relative z-10">
                    <h4 className="text-xl font-heading font-bold text-brand-gold mb-6 flex items-center gap-3">
                      <Icon name="CheckSquare" size={24} className="text-brand-gold" />
                      Liste de Vérification Finale
                    </h4>
                    <ul className="space-y-4">
                      {checklist?.map((item, index) => (
                        <li key={index} className="flex items-start gap-4 text-sm font-light text-white/80">
                          <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Icon name="Check" size={14} className="text-emerald-400" />
                          </div>
                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DocumentPreparation;